import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ArrowLeft } from "lucide-react";
import DharmaWheel from "../components/DharmaWheel";
import { api } from "../lib/api";

export default function Verify() {
  const { membershipId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/membership/verify/${encodeURIComponent(membershipId)}`);
        if (alive) setData(data);
      } catch (err) {
        if (alive) setError(err.response?.data?.detail || "Verification failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [membershipId]);

  return (
    <section className="bg-pearl min-h-[calc(100vh-5rem)] py-16 px-4">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-navy/70 hover:text-navy text-sm mb-8" data-testid="verify-home">
          <ArrowLeft size={14} /> Home
        </Link>
        <div className="bg-white border border-navy/10 rounded-sm p-10 text-center">
          <div className="flex justify-center mb-6">
            <DharmaWheel size={48} color="#001B3A" ringColor="#CFA052" />
          </div>
          <p className="eyebrow">Membership verification</p>
          {loading && <p className="mt-4 text-slate-500">Verifying…</p>}
          {error && (
            <div data-testid="verify-error">
              <div className="mt-6 flex justify-center text-saffron">
                <ShieldAlert size={48} strokeWidth={1.5} />
              </div>
              <h2 className="mt-4 font-display text-3xl text-navy">Not a valid member ID</h2>
              <p className="mt-2 text-slate-600 text-sm">{error}</p>
              <p className="mt-4 text-xs font-mono text-slate-400">{membershipId}</p>
            </div>
          )}
          {data && (
            <div data-testid="verify-success">
              <div className="mt-6 flex justify-center text-saffron">
                <ShieldCheck size={48} strokeWidth={1.5} />
              </div>
              <h2 className="mt-4 font-display text-3xl text-navy">Verified Member</h2>
              <p className="mt-2 text-saffron text-sm font-semibold">{data.tier_label}</p>
              <div className="mt-6 space-y-2 text-sm">
                <Row label="Name" value={data.name} />
                <Row label="Member ID" value={<span className="font-mono">{data.membership_id}</span>} />
                <Row label="Status" value={data.membership_status} />
                <Row label="Voting eligible" value={data.voting_eligible ? "Yes" : "No"} />
                {data.tier_active_since && (
                  <Row label="Member since" value={new Date(data.tier_active_since).toLocaleDateString("en-GB")} />
                )}
              </div>
              <p className="mt-8 text-xs text-slate-500 italic">
                Issued by the Ambedkarite Buddhist Global Network UK. This page is publicly accessible.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center border-b border-navy/5 py-2">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <span className="text-navy">{value}</span>
    </div>
  );
}
